import {Box, BoxProps, Flex, FlexProps} from '@chakra-ui/react';
import {motion} from 'framer-motion';

import {PlayerStatsModal} from '@eksab/features/competition/components/modals';

import {GroundMarksIcon, PlayerSpot} from './';
import type {Player, Players} from '../../types';
import {useLineupStore} from '../../lineup/useLineupStore';

const PLAYGROUND_ASPECT_RATIO = '580 / 473';

type LineupProps = {
  players?: Players;
  showStatsModal?: boolean;
} & BoxProps;

export function PlaygroundLineup({
  players = [[null], [null, null, null, null], [null, null, null, null], [null, null]],
  showStatsModal = true,
  ...props
}: LineupProps) {
  const [[goalKeeper], ...playerGroups] = players;

  const setPlayerStatsModal = useLineupStore((state) => state.setPlayerStatsModal);

  const onPlayerClick = showStatsModal
    ? (player: Player) => {
        mixpanel?.track('Button Clicked', {button: 'competition.show_player_info'});
        if (player) setPlayerStatsModal({player, context: 'lineup-player', action: 'remove'});
      }
    : undefined;

  return (
    <Box
      p={[1, null, 2]}
      w="full"
      sx={{aspectRatio: PLAYGROUND_ASPECT_RATIO}}
      backgroundColor="green.400"
      roundedTop="md"
      overflow="hidden"
      {...props}
    >
      <Box pos="relative" w="full" h="full" zIndex={1}>
        <GroundMarksIcon pos="absolute" inset={0} zIndex={-1} />

        <Box
          data-testid="playground"
          className="player-groups"
          h="full"
          p={[0, null, null, 1]}
          display="flex"
          flexDir="column"
          gap="3.3%"
          justifyContent="space-between"
        >
          <Box className="player-group" w="full" minH="20%" flex={1} display="flex" justifyContent="center">
            <PlayerSpot data-testid={'0-0'} player={goalKeeper} onPlayerInfoClick={onPlayerClick} />
          </Box>

          {playerGroups.map((playerGroup: (Player | null)[], lineIndex) => (
            <Box
              className="player-group"
              key={`${lineIndex}-${playerGroup.length}`}
              w="full"
              minH="20%"
              flex={1}
              display="flex"
              justifyContent="center"
              gap="min(1%, 0.375rem)"
              px="2%"
            >
              {playerGroup.map((player, playerIndex) => (
                <PlayerSpot
                  data-testid={`${lineIndex + 1}-${playerIndex}`}
                  key={player?.id ?? playerIndex}
                  player={player}
                  onPlayerInfoClick={onPlayerClick}
                />
              ))}
            </Box>
          ))}
        </Box>
      </Box>

      <PlayerStatsModal hasActions={false} />
    </Box>
  );
}

const Shimmer = (props: FlexProps) => (
  <Flex
    as={motion.div}
    shrink="0"
    w="full"
    roundedTop="md"
    sx={{aspectRatio: PLAYGROUND_ASPECT_RATIO}}
    animate={{
      backgroundColor: ['#f3f3f3', '#ecebeb'],
      transition: {duration: 1, repeat: Infinity, repeatType: 'reverse'},
    }}
    {...props}
  />
);

PlaygroundLineup.Shimmer = Shimmer;
